import React, { useEffect, useState } from "react";
import { IonModal } from "@ionic/react";
import "./Onboarding.css";
import SetGoalsScreen from "./SubComponents/SetDeviceGoals";
import { Preferences } from "@capacitor/preferences";
import { InfoArcConnect } from "./SubComponents/InfoArcConnect";
import { UserInfo } from "./SubComponents/UserInfo";
import DeviceUsageConfirm from "./SubComponents/DeviceUsageConfirm";
import QrScan from "./SubComponents/QrScan";
import AddAnotherDevice from "./SubComponents/AddAnotherDevice";
import CompleteIntro from "./SubComponents/CompleteIntro";
import HealthConnect from "./SubComponents/HealthConnect";
import TrackTreatments from "./SubComponents/TrackTreatments";
import useAuth from "../../hooks/useAuth";
import { updateProfile, updateUserPreferences } from "../../services/user";
import { createGoal, setDefaultGoalsForProfile } from "../../services/goals";
import { BiwazeEnum } from "../../utils/constants";
import { SecurityRequired } from "./SubComponents/SecurityRequired";
import { getUserDevices } from "../../services/device";
export const STEP_IDS = {
  INFO_ARC_CONNECT: "info-arc-connect",
  USER_INFO: "user-info",
  DEVICE_USAGE_CONFIRM: "device-usage-confirm",
  QR_SCAN: "qr-scan",
  ADD_ANOTHER_DEVICE: "add-another-device",
  SET_GOALS_COUGH: "set-goals-cough",
  SET_GOALS_CLEAR: "set-goals-clear",
  HEALTH_CONNECT: "health-connect",
  TRACK_TREATMENTS: "track-treatments",
  COMPLETE_INTRO: "complete-intro",
  SECURITY_REQUIRED: "security-required",
} as const;
interface ArcOnboardingProps {
  refresh?: () => void;
}

const ArcOnboarding: React.FC<ArcOnboardingProps> = ({ refresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  //add condition from prefrences if the user is new or old
  // Form data state
  const { user, updateUserProfile, platFormType } = useAuth();
  const [first_name, setFirstName] = useState(user?.first_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [CountryCode, setCountryCode] = useState(
    user?.phone_country_code || "",
  );
  const [last_name, setLastName] = useState(user?.last_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [usesBiWazeDevice, setUsesBiWazeDevice] = useState(
    "" as BiwazeEnum | "",
  );
  // Add more state variables for additional steps as needed
  // Add these at the top with other state variables
  const [connectedDevices, setConnectedDevices] = useState<any[]>([]);

  useEffect(() => {
    fetchDevices();
    setIsOpen(true);
  }, []);

  const fetchDevices = async () => {
    try {
      if (user?.profile_id) {
        const fetchedDevices = await getUserDevices(user.profile_id);
        console.log("Fetched devices:", fetchedDevices);
        setConnectedDevices(fetchedDevices);
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };
  useEffect(() => {
    loadSavedStep();
  }, []);

  const loadSavedStep = async () => {
    const { value } = await Preferences.get({ key: "onboardingStep" });
    if (value) {
      setCurrentStep(parseInt(value));
    }
  };

  const saveStep = async (step: number) => {
    await Preferences.set({
      key: "onboardingStep",
      value: step.toString(),
    });
  };
  // Add this function before the steps definition
  const handleGoalSet = (
    activeDevice: string,
    timesPerDay: number,
    therapyMinutes: number,
  ) => {
    const currentDate = new Date().toISOString().split("T")[0]; // Get today's date

    let goalType = "";
    const measurementUnit = "count";

    // Determine goal type based on the active device
    if (activeDevice === BiwazeEnum.COUGH) {
      goalType = "biwaze_cough";
    } else if (activeDevice === BiwazeEnum.CLEAR) {
      goalType = "biwaze_clear";
    }

    // Prepare goal data
    const goalData = {
      isActive: true,
      target_count: timesPerDay,
      target_seconds:
        activeDevice === BiwazeEnum.CLEAR
          ? therapyMinutes ?? 10 * 60
          : undefined,
      target_hours: undefined,
      goal_type: goalType,
      measurement_unit: measurementUnit,
      days_of_week: [], // All days of the week
      effective_from: currentDate,
    };
    const goalDataToSubmit = {
      ...goalData,
      profile_id: user?.id as number,
      device_id:
        connectedDevices.length > 0
          ? connectedDevices.find((device) => device.type === activeDevice)?.id
          : undefined,
    };

    createGoal(goalDataToSubmit);
    nextStep();
  };
  const closeModal = async () => {
    await Preferences.set({ key: "onboardingSkipped", value: "true" });
    setIsOpen(false);
    refresh && refresh();
  };

  const nextStep = (index?: number) => {
    if (index !== undefined && index >= 0 && index < steps.length) {
      setCurrentStep(index);
      saveStep(index);
      return;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep((prevStep) => prevStep + 1);
      saveStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prevStep) => Math.max(0, prevStep - 1));
      saveStep(currentStep - 1);
    }
  };

  const handleSkipSetup = async () => {
    // Skip the entire setup
    await Preferences.set({ key: "onboardingSkipped", value: "true" });

    closeModal();
  };

  const handleComplete = (index?: string | number) => {
    // Process all collected data
    console.log("Onboarding completed with data:", {
      first_name,
      last_name,
      usesBiWazeDevice,
    });
    closeModal();
  };

  const handleHealthConnect = async () => {
    await setDefaultGoalsForProfile(["step_tracking", "sleep_tracking"]);
    handleNavigateToStep(STEP_IDS.TRACK_TREATMENTS);
  };
  const handleTrackTreatments = async (selectedTreatments: string[]) => {
    console.log("Track Treatments setup completed", selectedTreatments);
    nextStep();
    const prefsPayload = {
      other_treatments: selectedTreatments.reduce(
        (acc, item) => ({ ...acc, [item]: true }),
        {},
      ),
    };
    await updateUserPreferences(prefsPayload);
    if (selectedTreatments.length === 0) {
      return;
    }
    await setDefaultGoalsForProfile(selectedTreatments);
  };
  // const handleTouchEnd = () => {
  //   if (!touchStart || !touchEnd) return;

  //   const distance = touchStart - touchEnd;
  //   const minSwipeDistance = 50; // Minimum distance to be considered a swipe

  //   // Swipe left (next)
  //   if (distance > minSwipeDistance) {
  //     nextStep();
  //   }

  //   // Swipe right (previous)
  //   else if (distance < -minSwipeDistance) {
  //     prevStep();
  //   }
  //   setTouchStart(null);
  //   setTouchEnd(null);
  // };

  const handleUserDataUpdate = async () => {
    const updatedProfile = {
      first_name,
      last_name,
      // email,
      // phone,
    };
    await updateProfile(updatedProfile);
    updateUserProfile(updatedProfile);
  };
  const handleNavigateToStep = (stepId: string) => {
    console.log("Navigating to step:", stepId);
    const stepIndex = steps.findIndex((step) => step.id === stepId);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
      saveStep(stepIndex);
    }
  };

  // Define your steps here
  const steps = [
    // Step 0: Welcome screen

    {
      id: STEP_IDS.INFO_ARC_CONNECT,
      render: () => (
        <InfoArcConnect nextStep={nextStep} closeModal={closeModal} />
      ),
    },
    // Step 1: Name input
    {
      id: STEP_IDS.USER_INFO,

      render: () => (
        <UserInfo
          closeModal={closeModal}
          nextStep={() => {
            nextStep();
            handleUserDataUpdate();
          }}
          prevStep={prevStep}
          steps={steps}
          currentStep={currentStep}
          first_name={first_name}
          setFirstName={setFirstName}
          last_name={last_name}
          setLastName={setLastName}
          countryCode={CountryCode}
          setCountryCode={setCountryCode}
          email={email}
          setEmail={setEmail}
          phone={phone}
          setPhone={setPhone}
          user={user}
        />
      ),
    },
    // Step 2: BiWaze device question
    {
      id: STEP_IDS.DEVICE_USAGE_CONFIRM,
      render: () => (
        <DeviceUsageConfirm
          usesBiWazeDevice={usesBiWazeDevice}
          first_name={first_name}
          prevStep={prevStep}
          nextStep={nextStep}
          handleSkipSetup={handleSkipSetup}
          handleComplete={handleComplete}
          setUsesBiWazeDevice={setUsesBiWazeDevice}
          currentStep={currentStep}
          steps={steps}
          handleNavigateToStep={handleNavigateToStep}
          devices={connectedDevices.filter((device) => device.type)}
        />
      ),
    },
    // Step 3: QR code scanning for BiWaze device
    {
      id: STEP_IDS.QR_SCAN,
      render: () => (
        <QrScan
          usesBiWazeDevice={usesBiWazeDevice}
          prevStep={prevStep}
          nextStep={nextStep}
          // handleSkipSetup={handleSkipSetup}
          handleComplete={handleComplete}
          currentStep={currentStep}
          steps={steps}
        />
      ),
    },
    // Step 4: Add another device
    {
      id: STEP_IDS.ADD_ANOTHER_DEVICE,
      render: () => (
        <AddAnotherDevice
          prevStep={prevStep}
          nextStep={nextStep}
          connectedDevices={connectedDevices}
          setConnectedDevices={setConnectedDevices}
          usesBiWazeDevice={usesBiWazeDevice}
          setUsesBiWazeDevice={setUsesBiWazeDevice}
          navigateToStep={handleNavigateToStep}
        />
      ),
    },
    // Step 5: Set goals for connected BiWaze device(s).
    // If user connects only cough, show goals for cough.
    // If clear, show goals for clear.
    // If both, show goals for both devices (duplicate below).
    // If none, skip to Health Connect step.
    ...connectedDevices
      .filter(
        (device) =>
          device.name === BiwazeEnum.COUGH || device.name === BiwazeEnum.CLEAR,
      )
      .map((device) => ({
        id:
          device.name === BiwazeEnum.COUGH
            ? STEP_IDS.SET_GOALS_COUGH
            : STEP_IDS.SET_GOALS_CLEAR,
        render: () => (
          <SetGoalsScreen
            onBack={() => handleNavigateToStep(STEP_IDS.DEVICE_USAGE_CONFIRM)}
            onNext={(activeDevice, timesPerDay, goalData) =>
              handleGoalSet(activeDevice, timesPerDay, goalData.target_seconds)
            }
            deviceName={
              device.name === BiwazeEnum.COUGH
                ? "BiWaze® Cough"
                : device.name === BiwazeEnum.CLEAR
                ? "BiWaze® Clear"
                : ""
            }
            nextStep={nextStep}
            onSkip={handleComplete}
            devices={connectedDevices.map((d) => d.type)}
            activeDevice={device.name}
          />
        ),
      })),
    // Step 6: Health Connect setup
    {
      id: STEP_IDS.HEALTH_CONNECT,
      render: () => (
        <HealthConnect
          prevStep={() => {
            // If no devices are connected, go back to device usage confirm
            if (connectedDevices.length === 0) {
              handleNavigateToStep(STEP_IDS.DEVICE_USAGE_CONFIRM);
            } else {
              prevStep();
            }
          }}
          handleContinue={handleHealthConnect}
          nextStep={nextStep}
          skipStep={handleSkipSetup}
          currentStep={currentStep}
          steps={steps}
          platform={platFormType as "ios" | "android" | "web"} // Pass the platform as a prop
        />
      ),
    },
    // Step 7: Track Treatments
    {
      id: STEP_IDS.TRACK_TREATMENTS,
      render: () => (
        <TrackTreatments
          nextStep={nextStep}
          prevStep={prevStep}
          proceed={handleTrackTreatments}
          currentStep={currentStep}
          steps={steps}
        />
      ),
    },
    // Step 8: Completion screen
    {
      id: STEP_IDS.COMPLETE_INTRO,
      render: () => (
        <CompleteIntro
          currentStep={currentStep}
          prevStep={prevStep}
          nextStep={nextStep}
          // handleComplete={handleComplete}
          steps={steps}
        />
      ),
    },
    // Step 9: Security Required screen
    {
      id: STEP_IDS.SECURITY_REQUIRED,
      render: () => (
        <SecurityRequired closeModal={handleComplete} prevStep={prevStep} />
      ),
    },
  ];
  if (!isOpen) {
    return null;
  }
  return (
    <IonModal
      isOpen={isOpen}
      backdropDismiss={false}
      className={`${
        platFormType === "web"
          ? "web-centered-modal bg-white"
          : "arc-onboarding-modal"
      }`}
    >
      <div className="status-background"></div>
      <div
        // onTouchStart={handleTouchStart}
        // onTouchMove={handleTouchMove}
        // onTouchEnd={handleTouchEnd}
        style={{ width: "100%", height: "100%" }}
      >
        {steps[currentStep]?.render()}
      </div>
    </IonModal>
  );
};

export default ArcOnboarding;
